import { useEffect, useRef, useState } from "react";
import { uploadExcelViaS3 } from "../../../utils/uploadExcelViaS3";
import { waitForUploadBatch } from "../../../utils/uploadWait";
import { isUploadBatchComplete } from "../../../utils/uploadPoll";
import { emitDashboardRefresh } from "../../../utils/dashboardEvents";
import {
  getUploadErrorMessage,
  validateUploadExcelFile,
} from "../../../utils/uploadExcelValidation";
import { parseUploadBatchId } from "../../../utils/uploadBatchId";
import DownloadSampleExcelButton from "../../../components/common/DownloadSampleExcelButton";
import {
  formatUploadProgressHint,
  formatUploadRowCount,
} from "../../../utils/uploadRowCount";

const STEPS = [
  { key: "select", label: "Select" },
  { key: "validate", label: "Validate" },
  { key: "upload", label: "Upload" },
  { key: "processing", label: "Process" },
  { key: "done", label: "Done" },
];

export default function BankRowUploadModal({
  open,
  onClose,
  token,
  bankName,
  branchName,
  onUploadSuccess,
}) {
  const fileInputRef = useRef(null);
  const uploadInFlight = useRef(false);

  const [file, setFile] = useState(null);
  const [step, setStep] = useState("select");
  const [error, setError] = useState("");

  const [uploadedFileName, setUploadedFileName] = useState("");
  const [fileRowCount, setFileRowCount] = useState(null);
  const [fileColumnCount, setFileColumnCount] = useState(null);
  const [processedRows, setProcessedRows] = useState(0);
  const [progressHint, setProgressHint] = useState("");

  const busy = step === "validate" || step === "upload" || step === "processing";

  useEffect(() => {
    if (!open) return;
    uploadInFlight.current = false;
    setFile(null);
    setStep("select");
    setError("");
    setUploadedFileName("");
    setFileRowCount(null);
    setFileColumnCount(null);
    setProcessedRows(0);
    setProgressHint("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [open, bankName, branchName]);

  if (!open) return null;

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0] || null;
    setFile(picked);
    setError("");
    setStep("select");
    setUploadedFileName(picked?.name || "");
    setFileRowCount(null);
    setFileColumnCount(null);
    setProcessedRows(0);
    setProgressHint("");
  };

  const handleClearFile = () => {
    if (busy) return;
    setFile(null);
    setUploadedFileName("");
    setStep("select");
    setError("");
    setFileRowCount(null);
    setFileColumnCount(null);
    setProcessedRows(0);
    setProgressHint("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (uploadInFlight.current || busy) return;
    if (!file) {
      setError("Please select an excel file first.");
      return;
    }

    uploadInFlight.current = true;
    setError("");
    setUploadedFileName(file.name);
    setStep("validate");
    setProgressHint("Checking selected file…");

    try {
      const localCheck = await validateUploadExcelFile(file);
      if (!localCheck.ok) {
        setStep("select");
        setError(localCheck.message);
        setProgressHint("");
        return;
      }

      setFileRowCount(localCheck.rowCount ?? null);
      setFileColumnCount(localCheck.columnCount ?? null);
      setStep("upload");
      setProgressHint(
        localCheck.deferredValidation
          ? "Large Excel — uploading. Headers are checked on the server…"
          : "Preparing upload…"
      );

      const { batchId, response } = await uploadExcelViaS3({
        file,
        bankName,
        branchName,
        token,
        onS3Progress: (loaded, total) => {
          if (total > 0) {
            const pct = Math.round((loaded / total) * 100);
            setProgressHint(`Uploading file… ${pct}%`);
          }
        },
      });

      setStep("processing");
      setProgressHint("Importing rows…");

      const batch = response?.data;
      const id = parseUploadBatchId(batch?._id || batchId);

      if (!id) {
        throw new Error("Upload did not return a batch id.");
      }

      const alreadyDone =
        !response?.processing && isUploadBatchComplete(batch);

      const done = alreadyDone
        ? batch
        : await waitForUploadBatch(id, token, {
              onProgress: (b) => {
                const total = Number(b?.totalRows) || 0;
                const processed = Number(b?.processedRows) || 0;
                // Show numeric counting only once import totals are known
                // (avoids a first 1000-by-1000 pass, then a second 25k pass).
                if (total > 0) {
                  setFileRowCount(total);
                  setProcessedRows(processed);
                }
                const hint = formatUploadProgressHint({
                  processedRows: total > 0 ? processed : 0,
                  totalRows: total,
                  message: b?.message || b?.importNote,
                });
                if (hint) setProgressHint(hint);
              },
            });

      if (alreadyDone) {
        emitDashboardRefresh({ batchId: id, batch: done });
      }

      if (onUploadSuccess) {
        onUploadSuccess({
          ...done,
          fileName: done?.fileName || file.name,
          bankName,
          branchName,
        });
      }

      setStep("done");
      if (done?.importNote) {
        setProgressHint(done.importNote);
        return;
      }

      setProgressHint(
        `Completed. Rows: ${Number(done?.totalRows || 0).toLocaleString()}, imported: ${Number(done?.successRows || 0).toLocaleString()}.`
      );
    } catch (err) {
      setStep("failed");
      setError(
        getUploadErrorMessage(
          err,
          "Upload failed. Please check the Excel file and try again."
        )
      );
      setProgressHint("");
    } finally {
      uploadInFlight.current = false;
    }
  };

  const boxState = (key) => {
    if (step === "failed") {
      if (key === "processing") return "error";
      if (key === "select" || key === "validate" || key === "upload") return "done";
      return "idle";
    }
    if (step === "done") return "done";
    if (key === "select") return file || step !== "select" ? "done" : "active";
    if (key === "validate") {
      if (step === "validate") return "active";
      if (step === "upload" || step === "processing") return "done";
      return "idle";
    }
    if (key === "upload") {
      if (step === "upload") return "active";
      if (step === "processing") return "done";
      return "idle";
    }
    if (key === "processing") {
      if (step === "processing") return "active";
      return "idle";
    }
    return "idle";
  };

  const showInfoBoxes = Boolean(uploadedFileName) && busy;

  return (
    <div
      className="modal-overlay bd-upload-modal-overlay"
      role="presentation"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="modal-box bd-upload-modal bd-upload-modal--compact"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bd-upload-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="bd-upload-modal-title">Upload records</h3>
        <p className="modal-subtitle">
          <strong>{bankName}</strong> — {branchName}
        </p>

        <div className="sample-excel-banner">
          <div className="sample-excel-banner__content">
            <p className="sample-excel-banner__title">Excel column layout</p>
            <p className="sample-excel-banner__desc">
              Download the sample template so your headers match the upload format.
            </p>
          </div>
          <DownloadSampleExcelButton disabled={busy} />
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="ur-file-input-hidden"
          onChange={handleFileChange}
        />

        <div className="bd-upload-panel">
          <div className="bd-upload-panel__row">
            <div className="bd-upload-panel__file">
              <span className="bd-upload-panel__label">Excel file</span>
              {file ? (
                <div className="bd-upload-panel__file-picked">
                  <span className="bd-upload-panel__file-name" title={uploadedFileName}>
                    {uploadedFileName}
                  </span>
                  <button
                    type="button"
                    className="bd-upload-panel__link"
                    disabled={busy}
                    onClick={handleClearFile}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="bd-upload-panel__choose"
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose file
                </button>
              )}
            </div>

            <button
              type="button"
              className="bd-upload-panel__start"
              disabled={!file || busy || step === "done"}
              onClick={handleUpload}
            >
              {busy ? "Uploading…" : step === "done" ? "Uploaded" : "Start upload"}
            </button>
          </div>

          <ol className="bd-upload-progress" aria-label="Upload progress">
            {STEPS.map((box, index) => {
              const state = boxState(box.key);
              const prevState = index > 0 ? boxState(STEPS[index - 1].key) : null;
              const connectorDone =
                prevState === "done" || state === "done" || state === "active";
              return (
                <li
                  key={box.key}
                  className={`bd-upload-progress__item bd-upload-progress__item--${state}`}
                >
                  {index > 0 ? (
                    <span
                      className={`bd-upload-progress__line${
                        connectorDone ? " bd-upload-progress__line--done" : ""
                      }`}
                      aria-hidden
                    />
                  ) : null}
                  <span className="bd-upload-progress__dot" aria-hidden>
                    {state === "done" ? "✓" : index + 1}
                  </span>
                  <span className="bd-upload-progress__name">{box.label}</span>
                </li>
              );
            })}
          </ol>
        </div>

        {showInfoBoxes && (
          <div className="bd-verify-info-boxes">
            <div className="bd-info-box bd-info-box--filename">
              <span className="bd-info-label">File name</span>
              <span className="bd-info-value" title={uploadedFileName}>
                {uploadedFileName}
              </span>
            </div>
            <div className="bd-info-box">
              <span className="bd-info-label">Rows</span>
              <span className="bd-info-value">
                {formatUploadRowCount({
                  processedRows,
                  totalRows: fileRowCount,
                })}
              </span>
            </div>
            <div className="bd-info-box">
              <span className="bd-info-label">Status</span>
              <span className="bd-info-value">
                {step === "validate"
                  ? "Validating"
                  : step === "upload"
                    ? "Uploading"
                    : "Processing"}
              </span>
            </div>
          </div>
        )}

        {progressHint && (busy || step === "done") && (
          <p className="bd-upload-progress-hint">{progressHint}</p>
        )}

        {error && (
          <div className="bd-upload-failed" role="alert">
            <p className="bd-upload-failed-title">Upload failed</p>
            <p className="bd-upload-failed-reason">
              <strong>Reason:</strong> {error}
            </p>
          </div>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="secondary-page-btn"
            onClick={onClose}
            disabled={busy}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
